import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createRestaurant } from '../services/menuService';
import { supabase } from '../services/supabase';
import { FiImage } from 'react-icons/fi';
import styles from '../styles/forms.module.css';

export default function RestaurantSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
  });
  const [logo, setLogo] = useState({
    file: null,
    preview: null
  });
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        alert('Logo image is too large. Please select an image under 2MB.');
        e.target.value = null; // Clear the input
        return;
      }
      
      setLogo({file: file, preview: null});
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(prev => ({...prev, preview: reader.result}));
      };
      reader.readAsDataURL(file);
    }
  };
  
  const uploadLogo = async (file, restaurantId) => {
    try {
      setUploading(true);
      
      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;
      const filePath = `restaurant-logos/${restaurantId}/${fileName}`;
      
      // Upload the file to the restaurant-assets bucket
      const { data, error } = await supabase.storage
        .from('restaurant-assets')
        .upload(filePath, file);
        
      if (error) throw error;
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('restaurant-assets')
        .getPublicUrl(filePath);
        
      return publicUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // First create the restaurant to get an ID
      const newRestaurant = await createRestaurant({
        ...formData,
        user_id: user.id
      });
      
      // Then upload logo if selected and update restaurant with logo URL
      if (logo.file) {
        const logoUrl = await uploadLogo(logo.file, newRestaurant.id);
        
        // Update restaurant with logo URL
        const { error } = await supabase
          .from('restaurants')
          .update({ logo_url: logoUrl })
          .eq('id', newRestaurant.id);
          
        if (error) throw error;
      }
      
      navigate(`/restaurant/${newRestaurant.id}/menu`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="banner">
        <h2>QR Menu App</h2>
      </div>
      
      <div className={styles.formContainer}>
        <h2>Set Up Your Restaurant</h2>
        <p>Create your restaurant profile to start managing your menu</p>

        {error && <div className={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Restaurant Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={styles.input}
              required
            />
          </div>
          
          <div className={styles.formGroup}>
            <label className={styles.label}>Logo (optional)</label>
            <div className={styles.imageUploadContainer}>
              <input
                type="file"
                id="restaurant-logo"
                accept="image/*"
                onChange={handleLogoChange}
                className={styles.fileInput}
              />
              <label htmlFor="restaurant-logo" className={styles.fileInputLabel}>
                <FiImage size={18} />
                {logo.file ? 'Change Logo' : 'Upload Logo'}
              </label>
            </div>
            
            {logo.preview && (
              <div className={styles.imagePreviewContainer}>
                <img 
                  src={logo.preview} 
                  alt="Logo Preview" 
                  className={styles.logoPreview} 
                />
              </div>
            )}
          </div>
          
          <div className={styles.formGroup}>
            <label className={styles.label}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={styles.textarea}
            />
          </div>
          
          <div className={styles.formGroup}>
            <label className={styles.label}>Address</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className={styles.input}
            />
          </div>
          
          <button 
            type="submit" 
            disabled={loading || uploading}
            className={styles.submitButton}
          >
            {loading || uploading ? 'Creating...' : 'Create Restaurant'}
          </button>
        </form>
      </div>
    </div>
  );
}